import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api from "../api/client.js";
import { useAuth } from "../context/AuthContext.jsx";
import { useI18n } from "../i18n/I18nContext.jsx";
import Dropdown from "../components/Dropdown.jsx";
import "../styles/Notifications.css";

export default function Notifications() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Role-based tab logic
  const isOfficer = user.role === "OFFICER" || user.role === "DEPT_HEAD";
  const isZonalAC = user.role === "ZONAL_AC";
  const isSuperAdmin = ["SUPER_ADMIN", "CORP_ADMIN", "AUDITOR"].includes(user.role);

  // Tabs for Zonal AC: "new_para" | "reply_submitted" | "all"
  // Tabs for Officer: "all" | "unread" | "read"
  // Tabs for Super Admin: "all" | "zone_wise" + zone filter
  const [activeTab, setActiveTab] = useState("all");
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    const params = { page_size: 200 };
    if (isSuperAdmin && selectedZone) {
      params.zone = selectedZone;
    }
    api
      .get("/notifications/", { params })
      .then((r) => {
        const data = r.data.results || r.data;
        setItems(data);
      })
      .finally(() => setLoading(false));
  }, [isSuperAdmin, selectedZone]);

  useEffect(() => {
    load();
  }, [load]);

  // Load zones for super admin
  useEffect(() => {
    if (isSuperAdmin) {
      api.get("/zones/").then((r) => setZones(r.data.results || r.data)).catch(() => {});
    }
  }, [isSuperAdmin]);

  const markAll = async () => {
    await api.post("/notifications/mark_all_read/");
    window.dispatchEvent(new Event("notifications_updated"));
    load();
  };

  const open = async (n) => {
    setSelected(n);
    if (!n.is_read) {
      await api.post(`/notifications/${n.id}/mark_read/`);
      window.dispatchEvent(new Event("notifications_updated"));
      load();
    }
  };

  const getLevelMeta = (level) => {
    if (level === "ESCALATION") return { color: "#c62828", bg: "#fdecea", icon: "⚠️", label: "Escalation" };
    if (level === "REMINDER") return { color: "#ef6c00", bg: "#fff3e0", icon: "⏰", label: "Reminder" };
    return { color: "#1565c0", bg: "#e8f1fb", icon: "ℹ️", label: "Info" };
  };

  const getTypeMeta = (type) => {
    if (type === "NEW_PARA") return { icon: "📋", label: "New Para", color: "#7c3aed", bg: "#f5f3ff" };
    if (type === "REPLY_SUBMITTED") return { icon: "📤", label: "Reply Submitted", color: "#0f766e", bg: "#f0fdfa" };
    return { icon: "🔔", label: "General", color: "#1565c0", bg: "#e8f1fb" };
  };

  // ── Filter logic per role ──────────────────────────────────────────────

  const getFilteredItems = () => {
    let base = items;

    // Search filter
    if (searchTerm) {
      base = base.filter(
        (n) =>
          n.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          n.para_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          n.zone_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (isOfficer) {
      // Officer: only their own notifications (API already filters), just tab by read/unread
      if (activeTab === "unread") return base.filter((n) => !n.is_read);
      if (activeTab === "read") return base.filter((n) => n.is_read);
      return base;
    }

    if (isZonalAC) {
      // Zonal AC: split by notification_type
      if (activeTab === "new_para") return base.filter((n) => n.notification_type === "NEW_PARA");
      if (activeTab === "reply_submitted") return base.filter((n) => n.notification_type === "REPLY_SUBMITTED");
      if (activeTab === "unread") return base.filter((n) => !n.is_read);
      return base; // "all"
    }

    // Super admin: all notifications, with optional zone filter (already done server-side)
    if (activeTab === "new_para") return base.filter((n) => n.notification_type === "NEW_PARA");
    if (activeTab === "reply_submitted") return base.filter((n) => n.notification_type === "REPLY_SUBMITTED");
    if (activeTab === "unread") return base.filter((n) => !n.is_read);
    return base;
  };

  const filteredItems = getFilteredItems();
  const unreadCount = items.filter((n) => !n.is_read).length;
  const newParaCount = items.filter((n) => n.notification_type === "NEW_PARA").length;
  const replyCount = items.filter((n) => n.notification_type === "REPLY_SUBMITTED").length;
  const hasUnread = unreadCount > 0;

  // ── Tab config per role ──────────────────────────────────────────────

  const getTabConfig = () => {
    if (isOfficer) {
      return [
        { key: "all", label: "All Notifications", count: items.length, icon: "🔔" },
        { key: "unread", label: "Unread", count: unreadCount, icon: "🔴", accent: true },
        { key: "read", label: "Read", count: items.length - unreadCount, icon: "✅" },
      ];
    }
    if (isZonalAC) {
      return [
        { key: "all", label: "All", count: items.length, icon: "🔔" },
        { key: "new_para", label: "New Para Assigned", count: newParaCount, icon: "📋", color: "#7c3aed" },
        { key: "reply_submitted", label: "Reply Submitted", count: replyCount, icon: "📤", color: "#0f766e" },
        { key: "unread", label: "Unread", count: unreadCount, icon: "🔴", accent: true },
      ];
    }
    // Super admin
    return [
      { key: "all", label: "All Notifications", count: items.length, icon: "🔔" },
      { key: "new_para", label: "Para Assignments", count: newParaCount, icon: "📋", color: "#7c3aed" },
      { key: "reply_submitted", label: "Replies Submitted", count: replyCount, icon: "📤", color: "#0f766e" },
      { key: "unread", label: "Unread", count: unreadCount, icon: "🔴", accent: true },
    ];
  };

  const tabs = getTabConfig();

  return (
    <div className="page-notifications">
      {/* ── Header ── */}
      <div className="notifications-header">
        <div>
        </div>
        {hasUnread && (
          <button
            className="btn-primary"
            onClick={markAll}
            style={{
              padding: "8px 16px", background: "#1e3a8a", color: "#fff",
              border: "none", borderRadius: "8px", fontSize: "12.5px",
              fontWeight: 600, cursor: "pointer", display: "flex",
              alignItems: "center", gap: 6,
            }}
          >
            ✓ {t("markAllRead")}
          </button>
        )}
      </div>

      {/* ── Role Badge ── */}
      <div style={{
        background: isOfficer ? "#eff6ff" : isZonalAC ? "#f5f3ff" : "#f0fdf4",
        border: `1px solid ${isOfficer ? "#bfdbfe" : isZonalAC ? "#e9d5ff" : "#86efac"}`,
        borderRadius: "10px", padding: "12px 18px", marginBottom: "16px",
        display: "flex", alignItems: "center", gap: 12,
      }}>
        <span style={{ fontSize: 20 }}>
          {isOfficer ? "👤" : isZonalAC ? "🏢" : "👑"}
        </span>
        <div>
          <div style={{ fontWeight: 700, fontSize: "13px", color: "#0f172a" }}>
            {isOfficer && "Officer View — Showing your personal notifications only"}
            {isZonalAC && `Zonal AC View — Notifications for ${user.zone_name || "your zone"}`}
            {isSuperAdmin && "Admin View — Full system-wide notification overview"}
          </div>
          <div style={{ fontSize: "11px", color: "#64748b", marginTop: 2 }}>
            {items.length} total · {unreadCount} unread
          </div>
        </div>
      </div>

      {/* ── Super Admin: Zone Selector ── */}
      {isSuperAdmin && (
        <div style={{
          background: "#fff", border: "1px solid #e2e8f0", borderRadius: "10px",
          padding: "14px 18px", marginBottom: "16px",
          display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
        }}>
          <div style={{ fontWeight: 700, fontSize: "12.5px", color: "#0f172a", display: "flex", alignItems: "center", gap: 6 }}>
            <span>🗺️</span> Zone Filter
          </div>
          <Dropdown
            value={selectedZone}
            onChange={(v) => setSelectedZone(v)}
            placeholder="All Zones"
            options={[
              { value: "", label: "All Zones" },
              ...zones.map((z) => ({ value: String(z.id), label: z.name })),
            ]}
          />
          {selectedZone && (
            <button
              onClick={() => setSelectedZone("")}
              style={{
                padding: "5px 12px", border: "1px solid #cbd5e1", borderRadius: "6px",
                background: "#fff", fontSize: "11.5px", cursor: "pointer", color: "#475569",
              }}
            >
              ✕ Clear
            </button>
          )}
          <div style={{ marginLeft: "auto", fontSize: "11px", color: "#64748b" }}>
            Showing <strong style={{ color: "#0f172a" }}>{filteredItems.length}</strong> notifications
            {selectedZone && ` for ${zones.find(z => String(z.id) === String(selectedZone))?.name || "selected zone"}`}
          </div>
        </div>
      )}

      {/* ── Tabs ── */}
      <div className="notif-tabs" style={{
        display: "flex", gap: "6px", background: "#fff",
        border: "1px solid #e2e8f0", borderRadius: "10px",
        padding: "8px", marginBottom: "16px", flexWrap: "wrap",
      }}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "7px 14px", borderRadius: "7px", border: "none",
              cursor: "pointer", fontSize: "12.5px", fontWeight: 600,
              transition: "all 0.15s",
              background: activeTab === tab.key
                ? (tab.color ? `${tab.color}18` : "#eff6ff")
                : "transparent",
              color: activeTab === tab.key
                ? (tab.color || "#1e3a8a")
                : "#475569",
              boxShadow: activeTab === tab.key ? `0 0 0 1.5px ${tab.color || "#1e3a8a"}40` : "none",
            }}
          >
            <span>{tab.icon}</span>
            {tab.label}
            <span style={{
              background: activeTab === tab.key
                ? (tab.color || "#1e3a8a")
                : "#e2e8f0",
              color: activeTab === tab.key ? "#fff" : "#475569",
              fontSize: "10px", fontWeight: 700,
              padding: "1px 7px", borderRadius: "99px",
            }}>
              {tab.count}
            </span>
          </button>
        ))}

        {/* Search */}
        <div className="notif-search-wrap" style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
          <input
            className="notif-search-input"
            placeholder="Search notifications..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              height: "34px", border: "1px solid #cbd5e1", borderRadius: "7px",
              padding: "0 12px", fontSize: "12px", minWidth: "180px",
              color: "#334155", background: "#f8fafc",
            }}
          />
        </div>
      </div>

      {/* ── Notifications List ── */}
      <div className="notifications-list">
        {loading && (
          <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8", fontSize: "13px" }}>
            ⏳ Loading notifications...
          </div>
        )}

        {!loading && filteredItems.length === 0 && (
          <div className="empty-statecard">
            <span className="empty-icon">🔕</span>
            <h3>No Notifications</h3>
            <p className="muted">
              {activeTab === "new_para" && "No new para assignment notifications."}
              {activeTab === "reply_submitted" && "No reply submission notifications."}
              {activeTab === "unread" && "All caught up! No unread notifications."}
              {activeTab === "all" && (searchTerm ? "No notifications match your search." : "You're all caught up!")}
              {activeTab === "read" && "No read notifications yet."}
            </p>
          </div>
        )}

        {!loading &&
          filteredItems.map((n) => {
            const levelMeta = getLevelMeta(n.level);
            const typeMeta = getTypeMeta(n.notification_type);

            return (
              <div
                key={n.id}
                className={`notif-card ${n.is_read ? "read" : "unread"}`}
                onClick={() => open(n)}
              >
                <div
                  className="notif-accent-line"
                  style={{
                    backgroundColor: n.notification_type === "NEW_PARA"
                      ? "#7c3aed"
                      : n.notification_type === "REPLY_SUBMITTED"
                      ? "#0f766e"
                      : levelMeta.color,
                  }}
                />
                {/* Type Icon */}
                <div
                  className="notif-icon-wrap"
                  style={{ color: typeMeta.color, backgroundColor: typeMeta.bg, fontSize: "1.2rem" }}
                >
                  {typeMeta.icon}
                </div>

                <div className="notif-content">
                  <div className="notif-message-row">
                    <span className="notif-message">{n.message}</span>
                    {!n.is_read && <span className="unread-dot" />}
                  </div>

                  <div className="notif-meta-row" style={{ gap: "8px", flexWrap: "wrap" }}>
                    {/* Type badge */}
                    <span style={{
                      display: "inline-flex", fontSize: "10px", fontWeight: 700,
                      padding: "2px 8px", borderRadius: "4px",
                      background: typeMeta.bg, color: typeMeta.color,
                      border: `1px solid ${typeMeta.color}40`,
                    }}>
                      {typeMeta.label}
                    </span>

                    {/* Level badge */}
                    <span className="notif-badge" style={{ color: levelMeta.color, borderColor: `${levelMeta.color}60` }}>
                      {n.level_display}
                    </span>

                    {/* Para No pill */}
                    {n.para_no && (
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        fontSize: "10px", fontWeight: 700, padding: "2px 8px",
                        borderRadius: "4px", background: "#f1f5f9", color: "#334155",
                        border: "1px solid #e2e8f0",
                      }}>
                        📄 Para {n.para_no}
                      </span>
                    )}

                    {/* Zone pill — for Super Admin */}
                    {isSuperAdmin && n.zone_name && (
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        fontSize: "10px", fontWeight: 700, padding: "2px 8px",
                        borderRadius: "4px", background: "#fef3c7", color: "#92400e",
                        border: "1px solid #fde68a",
                      }}>
                        🗺️ {n.zone_name}
                      </span>
                    )}

                    <span className="notif-time">
                      {new Date(n.created_at).toLocaleString("en-IN")}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      {/* ── Detail Modal ── */}
      {selected && createPortal(
        <div
          className="modal-overlay"
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 999,
          }}
          onClick={() => setSelected(null)}
        >
          <div
            style={{
              background: "#fff", borderRadius: "14px", padding: "28px",
              maxWidth: "460px", width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Type Badge */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <span style={{ fontSize: 28 }}>{getTypeMeta(selected.notification_type).icon}</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: "14px", color: "#0f172a" }}>
                  {getTypeMeta(selected.notification_type).label}
                </div>
                <div style={{ fontSize: "11px", color: "#64748b" }}>
                  {new Date(selected.created_at).toLocaleString("en-IN")}
                </div>
              </div>
            </div>

            <p style={{ fontSize: "15px", color: "#0f172a", lineHeight: 1.6, margin: "0 0 14px" }}>
              {selected.message}
            </p>

            {/* Meta info */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
              {selected.para_no && (
                <span style={{
                  padding: "4px 10px", borderRadius: "6px", fontSize: "11px",
                  fontWeight: 700, background: "#f1f5f9", color: "#334155",
                  border: "1px solid #e2e8f0",
                }}>
                  📄 Para {selected.para_no}
                </span>
              )}
              {selected.zone_name && (
                <span style={{
                  padding: "4px 10px", borderRadius: "6px", fontSize: "11px",
                  fontWeight: 700, background: "#fef3c7", color: "#92400e",
                  border: "1px solid #fde68a",
                }}>
                  🗺️ {selected.zone_name}
                </span>
              )}
              <span style={{
                padding: "4px 10px", borderRadius: "6px", fontSize: "11px",
                fontWeight: 700, background: getLevelMeta(selected.level).bg,
                color: getLevelMeta(selected.level).color,
              }}>
                {getLevelMeta(selected.level).icon} {selected.level_display}
              </span>
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              {selected.para && (
                <button
                  onClick={() => navigate(`/paras/${selected.para}`)}
                  style={{
                    padding: "9px 18px", background: "#1e3a8a", color: "#fff",
                    border: "none", borderRadius: "8px", fontWeight: 600,
                    fontSize: "13px", cursor: "pointer",
                  }}
                >
                  📋 View Para
                </button>
              )}
              <button
                onClick={() => setSelected(null)}
                style={{
                  padding: "9px 18px", background: "#f1f5f9", color: "#475569",
                  border: "1px solid #e2e8f0", borderRadius: "8px", fontWeight: 600,
                  fontSize: "13px", cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
